import { useSearchParams } from "react-router-dom";

import {
  isRuleDetailTabKey,
  RULE_DETAIL_TAB_PARAM,
  type RuleDetailTabKey,
} from "./ruleDetailTabs";

/**
 * The rule page's selected tab, held in the URL rather than in component
 * state: the tab is where the reader is, so a link can point at one and a
 * reload can restore it. Tab switches replace the entry so the back button
 * still leaves the rule rather than walking the tabs the reader visited.
 *
 * An unknown or absent `?tab=` falls back to the page's own default, which
 * differs by rule flavour — see ruleDetailTabs.
 *
 * A tab the org cannot see is treated as unknown: `?tab=advanced` on an org
 * without `advanced_agentic_logic_enabled` lands on the default rather than
 * rendering a body whose tab is not in the strip. The same clamp applies to
 * programmatic switches, so an in-app link to the Advanced tab cannot strand
 * the reader either.
 */
export const useRuleDetailTab = (
  defaultTab: RuleDetailTabKey,
  /**
   * Whether the org may reach the Advanced tab. Passed in rather than read
   * here: this hook is the URL seam and stays free of data providers, and the
   * page frame needs the same answer to draw the tab strip.
   */
  advancedEnabled: boolean,
  /**
   * Which query param carries the tab. Embedded surfaces namespace theirs away
   * from the host's own `tab` param (see `deriveEmbeddedPaths`).
   */
  tabParam: string = RULE_DETAIL_TAB_PARAM
): readonly [RuleDetailTabKey, (tab: RuleDetailTabKey) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isVisible = (tab: RuleDetailTabKey) =>
    tab !== "advanced" || advancedEnabled;
  const requested = searchParams.get(tabParam);
  const selectedTab =
    isRuleDetailTabKey(requested) && isVisible(requested)
      ? requested
      : defaultTab;

  const setSelectedTab = (tab: RuleDetailTabKey) => {
    if (!isVisible(tab)) {
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(tabParam, tab);
        return next;
      },
      { replace: true }
    );
  };

  return [selectedTab, setSelectedTab] as const;
};
